import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, Upload, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SettleModal({ open, onOpenChange, transaction, onSuccess }) {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    payment_date: new Date().toISOString().substring(0, 10),
    bank_account_id: '',
    payment_method_id: '',
    paid_amount: 0,
    interest_amount: 0,
    fine_amount: 0,
    discount_amount: 0,
    notes: '',
    attachment: null,
  });

  useEffect(() => {
    if (open && transaction) {
      setForm({
        payment_date: new Date().toISOString().substring(0, 10),
        bank_account_id: transaction.bank_account_id || '',
        payment_method_id: transaction.payment_method_id || '',
        paid_amount: transaction.amount || 0,
        interest_amount: 0,
        fine_amount: 0,
        discount_amount: 0,
        notes: '',
        attachment: null,
      });
      loadAux();
    }
  }, [open, transaction]);

  const loadAux = async () => {
    try {
      const [ba, pm] = await Promise.all([
        api.get('/financial/bank-accounts').catch(() => ({ data: { data: [] } })),
        api.get('/masters/payment-methods', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
      ]);
      const extract = (r) => {
        const d = r?.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.items)) return d.items;
        if (Array.isArray(d?.data)) return d.data;
        return [];
      };
      setBankAccounts(extract(ba));
      setPaymentMethods(extract(pm));
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 5MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm(f => ({
      ...f,
      attachment: { name: file.name, url, mime_type: file.type, size: file.size }
    }));
    toast.success('Comprovante anexado');
  };

  const netAmount = (parseFloat(form.paid_amount) || 0) +
    (parseFloat(form.interest_amount) || 0) +
    (parseFloat(form.fine_amount) || 0) -
    (parseFloat(form.discount_amount) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payment_date) { toast.error('Informe a data do pagamento'); return; }
    if (parseFloat(form.paid_amount) <= 0) { toast.error('Informe o valor pago'); return; }

    setSaving(true);
    try {
      const payload = {
        payment_date: form.payment_date,
        paid_amount: parseFloat(form.paid_amount),
        interest_amount: parseFloat(form.interest_amount) || 0,
        fine_amount: parseFloat(form.fine_amount) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        bank_account_id: form.bank_account_id || null,
        payment_method_id: form.payment_method_id || null,
        notes: form.notes || null,
        attachment: form.attachment || null,
      };

      await api.post(`/financial/transactions/${transaction.id}/settle`, payload);
      toast.success(transaction.type === 'revenue' ? 'Recebimento confirmado!' : 'Pagamento confirmado!');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao realizar baixa');
    } finally {
      setSaving(false);
    }
  };

  if (!transaction) return null;

  const isRevenue = transaction.type === 'revenue';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center font-bold ${
              isRevenue ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}>
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base">
                {isRevenue ? 'Confirmar Recebimento' : 'Confirmar Pagamento'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Registre a liquidação financeira no caixa ou conta bancária.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Resumo do Lançamento */}
        <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-neutral-500">Descrição:</span>
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">{transaction.description}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-500">Vencimento Original:</span>
            <span>{transaction.due_date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-500">Valor Original:</span>
            <span className="font-bold text-sm">{formatCurrency(transaction.amount)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Data da Baixa *</Label>
              <Input
                required
                type="date"
                value={form.payment_date}
                onChange={e => setForm({ ...form, payment_date: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Valor Principal (R$) *</Label>
              <Input
                required
                type="number"
                step="0.01"
                value={form.paid_amount}
                onChange={e => setForm({ ...form, paid_amount: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-neutral-500">Juros (+)</Label>
              <Input
                type="number"
                step="0.01"
                className="h-8 text-xs"
                value={form.interest_amount}
                onChange={e => setForm({ ...form, interest_amount: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-neutral-500">Multa (+)</Label>
              <Input
                type="number"
                step="0.01"
                className="h-8 text-xs"
                value={form.fine_amount}
                onChange={e => setForm({ ...form, fine_amount: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-neutral-500">Desconto (-)</Label>
              <Input
                type="number"
                step="0.01"
                className="h-8 text-xs"
                value={form.discount_amount}
                onChange={e => setForm({ ...form, discount_amount: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-xs">
            <span className="font-semibold text-neutral-600 dark:text-neutral-300">Valor Líquido Liquidado:</span>
            <span className={`text-sm font-bold ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(netAmount)}
            </span>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Conta Bancária de Liquidação</Label>
            <Select
              value={form.bank_account_id || 'none'}
              onValueChange={v => setForm({ ...form, bank_account_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger><SelectValue placeholder="Selecione a conta (atualiza saldo)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma (apenas registrar)</SelectItem>
                {bankAccounts.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.account_name} — Saldo: {formatCurrency(b.current_balance)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Forma de Pagamento</Label>
            <Select
              value={form.payment_method_id || 'none'}
              onValueChange={v => setForm({ ...form, payment_method_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger><SelectValue placeholder="Selecione a forma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                {paymentMethods.map(pm => <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Comprovante */}
          <div className="space-y-1.5">
            <Label className="text-xs">Comprovante de Pagamento</Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-8 text-xs gap-1">
                <Upload className="h-3.5 w-3.5" /> Anexar Comprovante
              </Button>
              <input ref={fileRef} type="file" onChange={handleFileUpload} className="hidden" />
              {form.attachment && (
                <span className="text-xs text-neutral-600 truncate max-w-[180px]">
                  {form.attachment.name}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Observações da Baixa</Label>
            <Textarea
              rows={2}
              placeholder="Opcional..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              type="submit"
              disabled={saving}
              className={isRevenue ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-brand hover:bg-[#A81E23] text-white'}
            >
              {saving ? 'Confirmando...' : isRevenue ? 'Confirmar Recebimento' : 'Confirmar Pagamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
